/**
 * El consentimiento GDPR entre la pantalla de alta y la sesión.
 *
 * El problema que resuelve: quien acepta y entra por SSO **se va del navegador**
 * —a Google, a Microsoft— y vuelve por `AuthCallbackPage`, que es otra carga de
 * la aplicación. El «acepto» no sobrevive a ese viaje en memoria, así que se
 * deja escrito y lo recoge `useSession` cuando llega el `SIGNED_IN`, que es el
 * primer momento en que hay una sesión a la que colgarlo (`record_gdpr_consent`).
 *
 * Vive en un módulo y no suelto en cada pantalla porque lo escriben dos sitios
 * —el alta y el diálogo— y lo lee y lo borra un tercero. Con el `try/catch`
 * copiado en cinco puntos, bastaba con que uno usara otra clave para que el
 * consentimiento se perdiera sin que nada fallara.
 */

const KEY = '__pending_gdpr_consent';

/** Deja constancia de que se ha aceptado, para cuando haya sesión. */
export function setPendingConsent(accepted: boolean): void {
  try {
    if (accepted) localStorage.setItem(KEY, '1');
    else localStorage.removeItem(KEY);
  } catch {
    // Navegador sin almacenamiento (modo privado con cookies bloqueadas). Se
    // pierde el consentimiento y se volverá a pedir, que es el fallo correcto.
  }
}

/** ¿Hay un «acepto» pendiente de registrar contra la cuenta? */
export function hasPendingConsent(): boolean {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

/** Ya registrado en el servidor: se olvida. */
export function clearPendingConsent(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ver `setPendingConsent` */
  }
}
